import { AccountInvocationItem, Call, transaction } from 'starknet'
import { CLPool, Transaction } from '../atoms'
import BigNumber from 'bignumber.js'

import { fromUint256, getToken, toDisplay, toUint256, toWei } from '../model/tokens/helper'
import { accountBraavosMultisig, accountCairoVersion, getAMMContract, getProvider, numberToCallParam, walletAddress } from './amm'
import { getNFTFactoryAddress } from './nft_pool'
import { Network } from './token.service'
import { MAX_UINT_128, getStarknet, getCurrentTick, FAIL_ALL_TXS, FAIL_CONTRACT } from './amm_v3'
import { CLPosition } from '../model/clpool'
import { getAllCLPoolForFee, getFactoryAddress } from '../model/clpool/helper'
import { BulkCall, bulkCallContract } from '../blockchainUtils/bulkCall'
import { MAX_TICK } from '../components/PoolsPage/helper'

export interface PositionValue {
    // the following tokens are same as the POOL tokens:
    token1Address: string
    token2Address: string
    token1AmountWei: string // amount*10^decimals
    token1Amount: string // "1.000" format
    token2AmountWei: string // amount*10^decimals
    token2Amount: string // "1.000" format
}

export const simulateClosePosition = async (
    network: Network,
    position: {
        positionIndex: number
        token0: string
        token1: string
        fee: number
        tickLower: number
        tickUpper: number
        liquidity: string
        feeGrowthInside0LastX128: string
        feeGrowthInside1LastX128: string
        tokensOwed0: string
        tokensOwed1: string
    },
    collectOnly: boolean = false,
    wallet: string
): Promise<PositionValue | undefined> => {
    const starknet = await getStarknet()

    // const storageWalletAddress = getStorageWallet()
    // ------------------------------------------
    let activeAccount: string = wallet
    // let activeAccount: string = storageWalletAddress ? storageWalletAddress : walletAddress()
    // if (!activeAccount) {
    //     return undefined
    // }
    if (!starknet?.isConnected) throw new Error('starknet wallet not connected')
    // ------------------------------------------

    const tokenId = position.positionIndex // TODO will change to tokenId

    const token_a = getToken(network, position.token0)
    const token_b = getToken(network, position.token1)
    if (token_a === undefined || token_b === undefined) {
        throw new Error('Unknown tokens in position')
    }

    const cairoVersion = accountCairoVersion()

    const nonce = await (await getProvider(network)).getNonceForAddress(activeAccount)
    const handler = cairoVersion === '1' ? transaction.fromCallsToExecuteCalldata_cairo1 : transaction.fromCallsToExecuteCalldata
    let calls = collectOnly
        ? handler([
              {
                  contractAddress: getNFTFactoryAddress(network),
                  entrypoint: 'collect',
                  calldata: [numberToCallParam(tokenId), 0, activeAccount, MAX_UINT_128, MAX_UINT_128]
              }
          ])
        : handler([
              {
                  contractAddress: getNFTFactoryAddress(network),
                  entrypoint: 'decrease_liquidity',
                  calldata: [numberToCallParam(tokenId), 0, numberToCallParam(position.liquidity), ...toUint256('0'), ...toUint256('0')]
              },
              {
                  contractAddress: getNFTFactoryAddress(network),
                  entrypoint: 'collect',
                  calldata: [numberToCallParam(tokenId), 0, activeAccount, MAX_UINT_128, MAX_UINT_128]
              }
          ])

    let invocation: AccountInvocationItem = {
        signature: [],
        // @ts-ignore
        type: 'INVOKE_FUNCTION',
        nonce: nonce,
        entrypoint: '__execute__',
        contractAddress: activeAccount,
        calldata: calls,
        version: '340282366920938463463374607431768211457'
    }
    const isBraavosMultisig = accountBraavosMultisig()
    if (isBraavosMultisig) {
        const wrapped_calls = handler([
            {
                contractAddress: activeAccount,
                entrypoint: 'sign_pending_multisig_transaction',
                calldata: [calls.length, ...calls, 0, 0, 1]
            }
        ])
        invocation = {
            signature: ['0', '0'],
            // @ts-ignore
            type: 'INVOKE_FUNCTION',
            nonce: nonce,
            entrypoint: '__execute__',
            contractAddress: activeAccount,
            calldata: wrapped_calls,
            version: '340282366920938463463374607431768211457'
        }
    }

    try {
        // in case it is a BraavosMultisig - we need to use the sequencer
        // as the rpc nodes dont support simulate txn version
        const provider = await getProvider(network, isBraavosMultisig)

        const response = await provider.getSimulateTransaction([invocation], {
            blockIdentifier: 'pending',
            skipValidate: true
        })

        // the result from the sequencer is different than the result
        // from the rpc
        // @ts-ignore
        const res = isBraavosMultisig ? response[0].transaction_trace.execute_invocation.result : response[0].transaction_trace.execute_invocation.result
        // const res = isBraavosMultisig ? response[0].transaction_trace.function_invocation.result : response[0].transaction_trace.execute_invocation.result

        const idx = res.length - 2
        const token1Amountwei = fromUint256(res[idx], '0')
        const token2Amountwei = fromUint256(res[idx + 1], '0')

        const v: PositionValue = {
            token1Address: token_a.address,
            token2Address: token_b.address,
            token1Amount: toDisplay(token1Amountwei, token_a),
            token1AmountWei: token1Amountwei.toFixed(0),
            token2Amount: toDisplay(token2Amountwei, token_b),
            token2AmountWei: token2Amountwei.toFixed(0)
        }
        return v
    } catch (e) {
        console.log('YSYS error in nft index:', position.positionIndex, ' collect only=', collectOnly, e)
    }
}

export const simulateCollectFeesOfPosition = async (
    network: Network,
    position: {
        positionIndex: number
        token0: string
        token1: string
        fee: number
        tickLower: number
        tickUpper: number
        liquidity: string
        feeGrowthInside0LastX128: string
        feeGrowthInside1LastX128: string
        tokensOwed0: string
        tokensOwed1: string
    },
    wallet: string
): Promise<PositionValue | undefined> => {
    return simulateClosePosition(network, position, true, wallet)
}

export const getPrincipalAndFeesOfPosition = async (
    network: Network,
    position: {
        positionIndex: number
        token0: string
        token1: string
        fee: number
        tickLower: number
        tickUpper: number
        liquidity: string
        feeGrowthInside0LastX128: string
        feeGrowthInside1LastX128: string
        tokensOwed0: string
        tokensOwed1: string
    },
    wallet: string,
    nonce: string
): Promise<{ fees: PositionValue; principal: PositionValue; total: PositionValue } | undefined> => {
    const starknet = await getStarknet()

    // ------------------------------------------
    let activeAccount: string = wallet
    if (!starknet?.isConnected) throw new Error('starknet wallet not connected')

    const tokenId = position.positionIndex // TODO will change to tokenId

    const token_a = getToken(network, position.token0)
    const token_b = getToken(network, position.token1)
    if (token_a === undefined || token_b === undefined) {
        throw new Error('Unknown tokens in position')
    }

    const cairoVersion = accountCairoVersion()

    const handler = cairoVersion === '1' ? transaction.fromCallsToExecuteCalldata_cairo1 : transaction.fromCallsToExecuteCalldata
    let calls = handler([
        {
            contractAddress: getNFTFactoryAddress(network),
            entrypoint: 'collect',
            calldata: [numberToCallParam(tokenId), 0, activeAccount, MAX_UINT_128, MAX_UINT_128]
        },
        {
            contractAddress: getNFTFactoryAddress(network),
            entrypoint: 'decrease_liquidity',
            calldata: [numberToCallParam(tokenId), 0, numberToCallParam(position.liquidity), ...toUint256('0'), ...toUint256('0')]
        },
        {
            contractAddress: getNFTFactoryAddress(network),
            entrypoint: 'collect',
            calldata: [numberToCallParam(tokenId), 0, activeAccount, MAX_UINT_128, MAX_UINT_128]
        }
    ])

    // const nonce = await (await getProvider(network)).getNonceForAddress(activeAccount);
    let invocation: AccountInvocationItem = {
        signature: [],
        // @ts-ignore
        type: 'INVOKE_FUNCTION',
        nonce: nonce,
        entrypoint: '__execute__',
        contractAddress: activeAccount,
        calldata: calls,
        version: '340282366920938463463374607431768211457'
    }
    const isBraavosMultisig = accountBraavosMultisig()
    if (isBraavosMultisig) {
        const wrapped_calls = handler([
            {
                contractAddress: activeAccount,
                entrypoint: 'sign_pending_multisig_transaction',
                calldata: [calls.length, ...calls, 0, 0, 1]
            }
        ])
        invocation = {
            signature: ['0', '0'],
            // @ts-ignore
            type: 'INVOKE_FUNCTION',
            nonce: nonce,
            entrypoint: '__execute__',
            contractAddress: activeAccount,
            calldata: wrapped_calls,
            version: '340282366920938463463374607431768211457'
        }
    }

    try {
        // in case it is a BraavosMultisig - we need to use the sequencer
        // as the rpc nodes dont support simulate txn version
        const provider = await getProvider(network, isBraavosMultisig)

        const response = await provider.getSimulateTransaction([invocation], {
            blockIdentifier: 'pending',
            skipValidate: true
        })

        // the result from the sequencer is different than the result
        // from the rpc
        // @ts-ignore
        const res = response[0].transaction_trace.execute_invocation.result

        // console.log('YSYS principal_and_fee ', res)
        const idx = res.length - 2
        const token1Amountwei = fromUint256(res[idx], '0')
        const token2Amountwei = fromUint256(res[idx + 1], '0')

        const principal: PositionValue = {
            token1Address: token_a.address,
            token2Address: token_b.address,
            token1Amount: toDisplay(token1Amountwei, token_a),
            token1AmountWei: token1Amountwei.toFixed(0),
            token2Amount: toDisplay(token2Amountwei, token_b),
            token2AmountWei: token2Amountwei.toFixed(0)
        }

        const idx_fee = cairoVersion === '0' ? res.length - 8 : res.length - 10
        // console.log('YSYS cairover=', cairoVersion, ' idx_fee=', idx_fee)
        const token1Amountwei_fee = fromUint256(res[idx_fee], '0')
        const token2Amountwei_fee = fromUint256(res[idx_fee + 1], '0')

        const fee: PositionValue = {
            token1Address: token_a.address,
            token2Address: token_b.address,
            token1Amount: toDisplay(token1Amountwei_fee, token_a),
            token1AmountWei: token1Amountwei_fee.toFixed(0),
            token2Amount: toDisplay(token2Amountwei_fee, token_b),
            token2AmountWei: token2Amountwei_fee.toFixed(0)
        }

        const total: PositionValue = {
            token1Address: token_a.address,
            token2Address: token_b.address,
            token1Amount: toDisplay(token1Amountwei.plus(token1Amountwei_fee), token_a),
            token1AmountWei: token1Amountwei.plus(token1Amountwei_fee).toFixed(0),
            token2Amount: toDisplay(token2Amountwei.plus(token2Amountwei_fee), token_b),
            token2AmountWei: token2Amountwei.plus(token2Amountwei_fee).toFixed(0)
        }
        return { fees: fee, principal: principal, total: total }
    } catch (e) {
        console.log('YSYS error in nft index:', position.positionIndex, e)
    }
}

export const collectFees = async (
    network: Network,
    position: {
        positionIndex: number
        token0: string
        token1: string
        fee: number
        tickLower: number
        tickUpper: number
        liquidity: string
        feeGrowthInside0LastX128: string
        feeGrowthInside1LastX128: string
        tokensOwed0: string
        tokensOwed1: string
    },
    simulate: boolean = false, // TODO: simulate should no be supported here
    // use simulateCollectFeesOfPosition() insteadsrc/services/position.ts

    token1Amount: string,
    token2Amount: string
): Promise<
    | PositionValue
    | {
          tx?: Transaction
          error?: string
          success: boolean
      }
    | undefined
> => {
    const starknet = await getStarknet()

    // ------------------------------------------
    // Omry: Is this the right code? null if no active but throw exception if not connected?
    // let activeAccount: string = walletAddress()
    // if (!activeAccount) {
    //     return undefined
    // }
    if (!starknet?.isConnected) throw Error('starknet wallet not connected')
    // ------------------------------------------

    const tokenId = position.positionIndex // TODO will change to tokenId
    const token_a = getToken(network, position.token0)
    const token_b = getToken(network, position.token1)

    if (token_a === undefined || token_b === undefined) {
        throw 'Unknown tokens in position'
    }

    const calls = [
        {
            contractAddress: getNFTFactoryAddress(network),
            entrypoint: 'collect',
            calldata: [numberToCallParam(tokenId), 0, starknet.account.address, MAX_UINT_128, MAX_UINT_128]
        }
    ]

    if (!simulate && FAIL_ALL_TXS) {
        calls.push({
            contractAddress: FAIL_CONTRACT,
            entrypoint: 'fail',
            calldata: []
        })
    }
    const handler = accountCairoVersion() === '1' ? transaction.fromCallsToExecuteCalldata_cairo1 : transaction.fromCallsToExecuteCalldata

    const invocation: AccountInvocationItem = {
        signature: [],
        // @ts-ignore
        type: 'INVOKE_FUNCTION',
        nonce: await (await getProvider(network)).getNonceForAddress(starknet.account.address),
        entrypoint: '__execute__',
        contractAddress: starknet.account.address,
        calldata: handler(calls)
    }

    if (simulate) {
        invocation.version = '340282366920938463463374607431768211457'
        try {
            const response = await (
                await getProvider(network, false)
            ).getSimulateTransaction([invocation], {
                blockIdentifier: 'pending',
                skipValidate: true
            })
            // console.log('YSYS get response ', response)

            // @ts-ignore
            const res = response[0].transaction_trace.execute_invocation.result
            // console.log('YSYS value result=', res)
            const idx = 1

            const token1Amountwei = fromUint256(res[idx], '0')
            const token2Amountwei = fromUint256(res[idx + 1], '0')

            const v: PositionValue = {
                token1Address: token_a.address,
                token2Address: token_b.address,
                token1Amount: toDisplay(token1Amountwei, token_a),
                token1AmountWei: token1Amountwei.toFixed(0),
                token2Amount: toDisplay(token2Amountwei, token_b),
                token2AmountWei: token2Amountwei.toFixed(0)
            }
            // console.log('YSYS v=', v)
            return v
        } catch (e) {
            console.log('YSYS error:', e)
        }
    }

    // invoke
    try {
        // @ts-ignore
        let tx = await starknet.account.execute(calls)

        const transaction: Transaction = {
            hash: tx.transaction_hash,
            network,
            status: 'new',
            token1: token_a.address,
            token1Amount: token1Amount,
            token2: token_b.address,
            token2Amount: token2Amount,
            type: 'collect',
            dismiss: false
        }

        return { tx: transaction, success: true }
    } catch (e) {
        return { error: 'Failed to collect', success: false }
    }
}

export const removeLiquidity = async (
    network: Network,
    position: {
        positionIndex: number
        token0: string
        token1: string
        fee: number
        tickLower: number
        tickUpper: number
        liquidity: string
        feeGrowthInside0LastX128: string
        feeGrowthInside1LastX128: string
        tokensOwed0: string
        tokensOwed1: string
    },
    positionValue: PositionValue, // needs to be up-to-date output of simulateClosePosition()
    fees: PositionValue, // needs to be up-to-date
    percent_to_remove: number, // 0-1 in percent of position.liquidity
    slippage: number, // percent in 0-1
    simulate: boolean = false
): Promise<
    | PositionValue
    | {
          tx?: Transaction
          error?: string
          success: boolean
      }
    | undefined
> => {
    const starknet = await getStarknet()

    console.log('percent_to_remove ', percent_to_remove)
    // ------------------------------------------
    // Omry: Is this the right code? null if no active but throw exception if not connected?
    let activeAccount: string = walletAddress()
    if (!activeAccount) {
        return undefined
    }
    if (!starknet?.isConnected) throw Error('starknet wallet not connected')
    // ------------------------------------------

    const tokenId = position.positionIndex // TODO will change to tokenId
    const token_a = getToken(network, position.token0)
    const token_b = getToken(network, position.token1)

    if (token_a === undefined || token_b === undefined) {
        throw 'Unknown tokens in position'
    }

    // calculate liq amount to remove
    const liq_to_remove = BigNumber(position.liquidity).multipliedBy(BigNumber(percent_to_remove)).toFixed(0)
    // we take position value minus fees, since the position value is
    // already with the fees, whereas the nft contract calculates
    // slippage before fees.
    const min_amount_a_wei = BigNumber(positionValue.token1AmountWei)
        .minus(BigNumber(fees.token1AmountWei))
        .multipliedBy(BigNumber(percent_to_remove))
        .multipliedBy(1 - slippage)
        .toFixed(0)
    const min_amount_b_wei = BigNumber(positionValue.token2AmountWei)
        .minus(BigNumber(fees.token2AmountWei))
        .multipliedBy(BigNumber(percent_to_remove))
        .multipliedBy(1 - slippage)
        .toFixed(0)

    // console.log('YSYS min_amount_a_wei ', min_amount_a_wei, ' vs position ', positionValue.token1AmountWei)
    // console.log('min_amount_b_wei ', min_amount_b_wei)

    const calls = [
        {
            contractAddress: getNFTFactoryAddress(network),
            entrypoint: 'decrease_liquidity',
            calldata: [numberToCallParam(tokenId), 0, numberToCallParam(liq_to_remove), ...toUint256(min_amount_a_wei), ...toUint256(min_amount_b_wei)]
        },
        {
            contractAddress: getNFTFactoryAddress(network),
            entrypoint: 'collect',
            calldata: [numberToCallParam(tokenId), 0, starknet.account.address, MAX_UINT_128, MAX_UINT_128]
        }
    ]

    if (percent_to_remove === 1) {
        calls.push({
            contractAddress: getNFTFactoryAddress(network),
            entrypoint: 'burn',
            calldata: [numberToCallParam(tokenId), 0]
        })

        // calls.push({ // remove this
        //     contractAddress: getFactoryAddress(network),
        //     entrypoint: 'delete_pool',
        //     calldata: ['0x663ea00ac01a50a5f53168a0a26dae38a8983ebea341f2e7cd10237293c56e']
        // })
    }

    if (!simulate && FAIL_ALL_TXS) {
        calls.push({
            contractAddress: FAIL_CONTRACT,
            entrypoint: 'fail',
            calldata: []
        })
    }

    const handler = accountCairoVersion() === '1' ? transaction.fromCallsToExecuteCalldata_cairo1 : transaction.fromCallsToExecuteCalldata

    const invocation: AccountInvocationItem = {
        signature: [],
        // @ts-ignore
        type: 'INVOKE_FUNCTION',
        nonce: await (await getProvider(network)).getNonceForAddress(starknet.account.address),
        entrypoint: '__execute__',
        contractAddress: starknet.account.address,
        calldata: handler(calls)
    }

    if (simulate) {
        invocation.version = '340282366920938463463374607431768211457'
        try {
            const response = await (
                await getProvider(network, false)
            ).getSimulateTransaction([invocation], {
                blockIdentifier: 'pending',
                skipValidate: true
            })
            // console.log('YSYS get response ', response)

            // @ts-ignore
            const res = response[0].transaction_trace.execute_invocation.result
            // console.log('YSYS value result=', res)
            const idx = 5

            const token1Amountwei = fromUint256(res[idx], '0')
            const token2Amountwei = fromUint256(res[idx + 1], '0')

            const v: PositionValue = {
                token1Address: token_a.address,
                token2Address: token_b.address,
                token1Amount: toDisplay(token1Amountwei, token_a),
                token1AmountWei: token1Amountwei.toFixed(0),
                token2Amount: toDisplay(token2Amountwei, token_b),
                token2AmountWei: token2Amountwei.toFixed(0)
            }
            // console.log('YSYS v=', v)
            return v
        } catch (e) {
            // console.log('YSYS error:', e)
        }
    }

    // invoke
    try {
        // @ts-ignore
        let tx = await starknet.account.execute(calls)

        const transaction: Transaction = {
            hash: tx.transaction_hash,
            network,
            status: 'new',
            token1: token_a.address,
            token1Amount: min_amount_a_wei,
            token2: token_b.address,
            token2Amount: min_amount_b_wei,
            type: 'remove_liquidity',
            dismiss: false
        }

        return { tx: transaction, success: true }
    } catch (e) {
        return { error: 'Failed to remove liquidity', success: false }
    }
}

export const addLiquidity = async (
    network: Network,
    position: {
        positionIndex: number
        token0: string
        token1: string
        fee: number
        tickLower: number
        tickUpper: number
        liquidity: string
        feeGrowthInside0LastX128: string
        feeGrowthInside1LastX128: string
        tokensOwed0: string
        tokensOwed1: string
    },
    positionValue: PositionValue, // needs to be up-to-date output of simulateClosePosition()
    token1Amount: string, // token1 amount, as decmial string (not wei)
    token2Amount: string,
    slippage: number, // percent in 0-1
    simulate: boolean = false
): Promise<
    | PositionValue
    | {
          tx?: Transaction
          error?: string
          success: boolean
      }
    | undefined
> => {
    const starknet = window.getStarknet?.()
    // await starknet?.enable({ starknetVersion: 'v5' })

    // ------------------------------------------
    // Omry: Is this the right code? null if no active but throw exception if not connected?
    // let activeAccount: string = walletAddress()
    // if (!activeAccount) {
    //     return undefined
    // }
    if (!starknet?.isConnected) throw Error('starknet wallet not connected')
    // ------------------------------------------

    const tokenId = position.positionIndex // TODO will change to tokenId
    const token_a = getToken(network, position.token0)
    const token_b = getToken(network, position.token1)

    if (token_a === undefined || token_b === undefined) {
        throw 'Unknown tokens in position'
    }

    // calculate amounts to add in wei
    const amount_a_wei = toWei(token1Amount, token_a)
    const amount_b_wei = toWei(token2Amount, token_b)

    const min_amount_a_wei = BigNumber(amount_a_wei)
        .multipliedBy(1 - slippage)
        .toFixed()
    const min_amount_b_wei = BigNumber(amount_b_wei)
        .multipliedBy(1 - slippage)
        .toFixed()

    const calls: Array<Call> = [
        {
            contractAddress: token_a.address,
            entrypoint: 'approve',
            calldata: [numberToCallParam(getNFTFactoryAddress(network)), ...toUint256(amount_a_wei)]
        },
        {
            contractAddress: token_b.address,
            entrypoint: 'approve',
            calldata: [numberToCallParam(getNFTFactoryAddress(network)), ...toUint256(amount_b_wei)]
        },
        {
            contractAddress: getNFTFactoryAddress(network),
            entrypoint: 'increase_liquidity',
            calldata: [numberToCallParam(tokenId), 0, ...toUint256(amount_a_wei), ...toUint256(amount_b_wei), ...toUint256(min_amount_a_wei), ...toUint256(min_amount_b_wei)]
        }
    ]

    if (!simulate && FAIL_ALL_TXS) {
        calls.push({
            contractAddress: FAIL_CONTRACT,
            entrypoint: 'fail',
            calldata: []
        })
    }

    if (simulate) {
        const handler = accountCairoVersion() === '1' ? transaction.fromCallsToExecuteCalldata_cairo1 : transaction.fromCallsToExecuteCalldata

        const invocation: AccountInvocationItem = {
            signature: [],
            // @ts-ignore
            type: 'INVOKE_FUNCTION',
            nonce: await (await getProvider(network)).getNonceForAddress(starknet.account.address),
            entrypoint: '__execute__',
            contractAddress: starknet.account.address,
            calldata: handler(calls),
            version: '340282366920938463463374607431768211457'
        }
        try {
            const response = await (
                await getProvider(network, false)
            ).getSimulateTransaction([invocation], {
                blockIdentifier: 'pending',
                skipValidate: true
            })
            // console.log('YSYS get response ', response)

            // @ts-ignore
            const res = response[0].transaction_trace.execute_invocation.result
            // console.log('YSYS value result=', res)
            const idx = 4

            const token1Amountwei = fromUint256(res[idx], res[idx + 1])
            const token2Amountwei = fromUint256(res[idx + 2], res[idx + 3])

            const v: PositionValue = {
                token1Address: token_a.address,
                token2Address: token_b.address,
                token1Amount: toDisplay(token1Amountwei, token_a),
                token1AmountWei: token1Amountwei.toFixed(0),
                token2Amount: toDisplay(token2Amountwei, token_b),
                token2AmountWei: token2Amountwei.toFixed(0)
            }
            // console.log('YSYS v=', v)
            return v
        } catch (e) {
            // console.log('YSYS error:', e)
        }
    }

    // invoke
    try {
        // @ts-ignore
        let tx = await starknet.account.execute(calls)
        const transaction: Transaction = {
            hash: tx.transaction_hash,
            network,
            status: 'new',
            token1: token_a.address,
            token1Amount: min_amount_a_wei,
            token2: token_b.address,
            token2Amount: min_amount_b_wei,
            type: 'add_liquidity',
            dismiss: false
        }

        return { tx: transaction, success: true }
    } catch (e: any) {
        // console.log('add liquidity error ', e.message)
        return { error: 'Failed to add liquidity', success: false }
    }
}

export const getLegacyPositions = async (network: Network): Promise<CLPosition[]> => {
    const starknet = await getStarknet()

    // ------------------------------------------
    let activeAccount: string = walletAddress()
    if (!activeAccount) {
        return []
    }
    if (!starknet?.isConnected) throw Error('starknet wallet not connected')
    // ------------------------------------------

    try {
        const response0 = await (
            await getProvider(network)
        ).callContract({
            entrypoint: 'get_account_positions',
            contractAddress: getLegacyMigratorAddress(network),
            calldata: [numberToCallParam(activeAccount)]
        })
        let num_legacy = Number(response0.result[0])

        if (num_legacy === 0) return []
        // generate the claim_all call for simulate()
        // note that we don't need to approve() the lp_token for each
        // legacy pool since the contract does not transfer
        // when in simulate mode

        const handler = accountCairoVersion() === '1' ? transaction.fromCallsToExecuteCalldata_cairo1 : transaction.fromCallsToExecuteCalldata

        let calls = handler([
            {
                contractAddress: getLegacyMigratorAddress(network),
                entrypoint: 'claim_all',
                calldata: []
            }
        ])

        const invocation: AccountInvocationItem = {
            signature: [],
            // @ts-ignore
            type: 'INVOKE_FUNCTION',
            nonce: await (await getProvider(network)).getNonceForAddress(starknet.account.address),
            entrypoint: '__execute__',
            contractAddress: starknet.account.address,
            calldata: calls,
            version: '340282366920938463463374607431768211457'
        }

        const response = await (
            await getProvider(network, false)
        ).getSimulateTransaction([invocation], {
            blockIdentifier: 'pending',
            skipValidate: true
        })

        // @ts-ignore
        const res = response[0].transaction_trace.execute_invocation.result

        let idx = 2

        const positions = Array<CLPosition>()

        while (num_legacy > 0) {
            const token_a = getToken(network, res[idx + 5])
            const token_b = getToken(network, res[idx + 6])

            if (!token_a || !token_b) {
                throw 'Unknown token a/b'
            }
            const token_a_amount_wei = new BigNumber(res[idx + 15])
            const token_b_amount_wei = new BigNumber(res[idx + 16])

            const fee = Number(res[idx + 7])
            const poolForPosition = await getAllCLPoolForFee(network, token_a, token_b, fee)
            if (new BigNumber(res[idx + 0]).isEqualTo(poolForPosition.address) === false) {
                throw 'mismatch in pool address'
            }
            const currentTick = await getCurrentTick(network, poolForPosition.address)

            const pos: CLPosition = {
                fee: fee,
                feeGrowthInside0LastX128: '0',
                feeGrowthInside1LastX128: '0',
                liquidity: res[idx + 10],
                positionIndex: -1,
                tickLower: Number(res[idx + 8]),
                tickUpper: Number(res[idx + 9]),
                token0: res[idx + 5],
                token1: res[idx + 6],
                tokensOwed0: res[idx + 15],
                tokensOwed1: res[idx + 16],
                fees: undefined,
                poolAddress: res[idx + 0],
                poolCurrentTick: currentTick || currentTick === 0 ? currentTick + MAX_TICK : -1,
                value: {
                    token1Address: res[idx + 5],
                    token1Amount: toDisplay(token_a_amount_wei, token_a),
                    token1AmountWei: token_a_amount_wei.toFixed(),
                    token2Address: res[idx + 6],
                    token2Amount: toDisplay(token_b_amount_wei, token_b),
                    token2AmountWei: token_b_amount_wei.toFixed()
                },
                legacyPoolId: Number(res[idx + 20]),
                legacyLpTokenAddress: res[idx + 17]
            }
            positions.push(pos)
            idx += 21 // total size of MigratedPool struct
            num_legacy -= 1
        }
        // console.log('YSYS positions=', positions)
        return positions
    } catch (e: any) {
        // console.log('YSYS legacy positions error ', e.message)
        return []
    }
}

export const claimLegacyPosition = async (
    network: Network,
    position: CLPosition,
    claimOrWithdraw: 'claim' | 'withdraw'
): Promise<{ success: boolean; error?: string; tx: Transaction | undefined }> => {
    const starknet = await getStarknet()

    // ------------------------------------------
    let activeAccount: string = walletAddress()
    if (!activeAccount) {
        return { success: false, error: 'no account', tx: undefined }
    }
    if (!starknet?.isConnected) throw Error('starknet wallet not connected')
    // ------------------------------------------

    try {
        const migrator = getLegacyMigratorAddress(network)
        // generate the claim invoke call
        // note that we need to approve() the lp_token to the migrator
        let calls: Array<Call> = [
            {
                contractAddress: position.legacyLpTokenAddress ?? '0x0',
                entrypoint: 'approve',
                calldata: [migrator, '340282366920938463463374607431768211455', '340282366920938463463374607431768211455']
            },
            {
                contractAddress: migrator,
                entrypoint: 'claim',
                calldata: [position.legacyPoolId ?? 0, claimOrWithdraw == 'claim' ? getNFTFactoryAddress(network) : 0]
            }
        ]

        // @ts-ignore
        let tx = await starknet.account.execute(calls)

        const transaction: Transaction = {
            hash: tx.transaction_hash,
            network,
            status: 'new',
            type: 'claim_legacy',
            dismiss: false,
            token1: '',
            token1Amount: '',
            token2: '',
            token2Amount: ''
        }

        return { tx: transaction, success: true }
    } catch (e: any) {
        // console.log('YSYS legacy positions error ', e.message)
        return { error: 'Failed to claim', tx: undefined, success: false }
    }

    // return { error: 'Failed to claim', success: false }
}

function getLegacyMigratorAddress(network: Network): string {
    if (network === 'goerli-alpha') return '0x07dae7c54d44ec2aac2da399469381d580c36c4d92ad11406464ac0aaf1c38ad'

    return '0x0'
}

export function getLegacyLpTokenAddresses(network: Network): Array<string> {
    if (network === 'goerli-alpha') {
        return [
            '0x895a214b06582050056ad5dd29dd8086a82d9159f023ab4d21d6fa1a2985eb',
            '0x1550ce85c20f616af7eec81b3a1c4d4cc2ca99140e68b8092256bdff15e7df9',
            '0x21312730d9bac8678adfaf52f7a4471decbca5b502ac05c223e627e78c7087a',
            '0x28189e3e0dfa5f17528dfb084f2d8637f7f2ec44e902de1f574ef3dbdc24a84'
        ]
    }
    if (network === 'mainnet-alpha') {
        return [
            '0x22b05f9396d2c48183f6deaf138a57522bcc8b35b67dee919f76403d1783136',
            '0x7c662b10f409d7a0a69c8da79b397fd91187ca5f6230ed30effef2dceddc5b3',
            '0x25b392609604c75d62dde3d6ae98e124a31b49123b8366d7ce0066ccb94f696',
            '0x41f9a1e9a4d924273f5a5c0c138d52d66d2e6a8bee17412c6b0f48fe059ae04',
            '0x1ea237607b7d9d2e9997aa373795929807552503683e35d8739f4dc46652de1',
            '0x611e8f4f3badf1737b9e8f0ca77dd2f6b46a1d33ce4eed951c6b18ac497d505',
            '0x14e644c20bd5f9888033d2093c8ba3334caa0c7d15ed142962a9bebf36cc7e0',
            '0x2699b69786cb08b4c83c1c02e943eca3eba00234d80a564ebe00c40226ea70b'
        ]
    }
    return []
}
export const getLegacyPositions_phase0 = async (network: Network): Promise<CLPosition[]> => {
    const starknet = await getStarknet()

    // ------------------------------------------
    let activeAccount: string = walletAddress()
    if (!activeAccount) {
        return []
    }
    if (!starknet?.isConnected) throw Error('starknet wallet not connected')
    // ------------------------------------------

    try {
        // read all my positions using hard-coded LP token addresses
        const lp_balance_calls = new Array<BulkCall>()
        let pool_id = 1

        const lp_addresses = getLegacyLpTokenAddresses(network)

        const pool_data: { [pool_id: number]: { balance: string; total: string; token0: string; token1: string; token0_balance: string; token1_balance: string } } = {}

        for (const lp_address of lp_addresses) {
            pool_data[pool_id] = {
                balance: '',
                total: '',
                token0: '',
                token0_balance: '',
                token1: '',
                token1_balance: ''
            }

            lp_balance_calls.push({
                call: {
                    entrypoint: 'balanceOf',
                    contractAddress: lp_address,
                    calldata: [numberToCallParam(activeAccount)]
                },
                responseParser: {
                    kind: 'fixed',
                    length: 2
                }
            })

            lp_balance_calls.push({
                call: {
                    entrypoint: 'totalSupply',
                    contractAddress: lp_address,
                    calldata: []
                },
                responseParser: {
                    kind: 'fixed',
                    length: 2
                }
            })
            lp_balance_calls.push({
                call: {
                    entrypoint: 'get_pool',
                    contractAddress: getAMMContract(network),
                    calldata: [pool_id]
                },
                responseParser: {
                    kind: 'fixed',
                    length: 10
                }
            })
            pool_id = pool_id + 1
        }

        const calls = await bulkCallContract(network, lp_balance_calls)
        calls.map((res, index) => {
            // console.log('YSYS bulk index=', index, 'res=', res)
            const pool_id = 1 + Math.floor(index / 3)
            if (index % 3 == 0) {
                // my pool balance
                pool_data[pool_id].balance = fromUint256(res.result[0], res.result[1]).toFixed(0)
            }

            if (index % 3 == 1) {
                // total pool balance
                pool_data[pool_id].total = fromUint256(res.result[0], res.result[1]).toFixed(0)
            }

            if (index % 3 == 2) {
                // pool data
                const r = res.result
                pool_data[pool_id].token0 = r[1]
                pool_data[pool_id].token0_balance = fromUint256(r[2], r[3]).toFixed(0)
                pool_data[pool_id].token1 = r[4]
                pool_data[pool_id].token1_balance = fromUint256(r[5], r[6]).toFixed(0)
            }
        })

        // console.log('YSYS phase0 pool data = ', pool_data)

        const positions = Array<CLPosition>()

        for (const pool_id in pool_data) {
            const data = pool_data[pool_id]
            // only use data with user balance
            if (BigNumber(data.balance).gt(0)) {
                const ratio = BigNumber(data.balance).dividedBy(BigNumber(data.total))
                const t0 = BigNumber(data.token0_balance).multipliedBy(ratio)
                const t1 = BigNumber(data.token1_balance).multipliedBy(ratio)
                // read the tokens as well
                const token0 = getToken(network, data.token0)
                const token1 = getToken(network, data.token1)

                if (!token0 || !token1) {
                    throw 'Unknown token a/b'
                }

                const pos: CLPosition = {
                    fee: 3000,
                    feeGrowthInside0LastX128: '0',
                    feeGrowthInside1LastX128: '0',
                    liquidity: '0',
                    positionIndex: -1,
                    tickLower: -1,
                    tickUpper: -1,
                    token0: data.token0,
                    token1: data.token1,
                    tokensOwed0: '0',
                    tokensOwed1: '0',
                    fees: undefined,
                    poolAddress: '0',
                    poolCurrentTick: -1,
                    value: {
                        token1Address: data.token0,
                        token1Amount: toDisplay(t0, token0),
                        token1AmountWei: t0.toFixed(),
                        token2Address: data.token1,
                        token2Amount: toDisplay(t1, token1),
                        token2AmountWei: t1.toFixed()
                    },
                    legacyPoolId: Number(pool_id),
                    legacyLpTokenAddress: lp_addresses[Number(pool_id) - 1],
                    legacyLpTokenAmount: data.balance
                }
                positions.push(pos)
            }
        }
        // console.log('YSYS phase0 legacy positions=', positions)
        return positions
    } catch (e: any) {
        // console.log('YSYS legacy positions error ', e.message)
        return []
    }
}

// return the target pool name
/*export const migrateLegacyPositionDestination_phase0 = (network: Network, position: CLPosition): string => {
    const { token0, token1 } = migrateLegacyPositionDestination_phase0_tokens(network, position)
    const fees = migrateLegacyPositionDestination_phase0_fees(network, position)
    if (token0 && token1 && fees) {
        return `${token0}/${token1} 0.3% to ${token0}/${token1} ${fees.new}%`
    }
    return 'Unknown pool'
}
*/

export const migrateLegacyPositionDestination_phase0_fees_and_range = (network: Network, position: CLPosition): { original: number; new: number; range: number } => {
    if (!position.legacyPoolId) throw 'Missing pool id'

    if (network === 'mainnet-alpha') {
        const fees = [0.05, 0.05, 0.3, 0.05, 0.01, 0.01, 0.01, 0.3]
        const ranges = [15, 15, 15, 15, 1, 1, 3, 15]
        if (position.legacyPoolId >= 1 && position.legacyPoolId <= 8) {
            return { original: 0.3, new: fees[position.legacyPoolId - 1], range: ranges[position.legacyPoolId - 1] }
        }
    }

    if (network === 'goerli-alpha') {
        const fees = [1, 0.01, 0.05, 0.01]
        const ranges = [15, 15, 15, 15]
        if (position.legacyPoolId >= 1 && position.legacyPoolId <= 4) {
            return { original: 0.3, new: fees[position.legacyPoolId - 1], range: ranges[position.legacyPoolId - 1] }
        }
    }

    throw 'Unknown pool id for migration'
}

export const migrateLegacyPositionDestination_phase0_tokens = (network: Network, position: CLPosition): { token0: string; token1: string } => {
    if (network === 'mainnet-alpha') {
        if (position.legacyPoolId === 1) {
            return { token0: 'ETH', token1: 'USDC' }
        }
        if (position.legacyPoolId === 2) {
            return { token0: 'DAI', token1: 'ETH' }
        }
        if (position.legacyPoolId === 3) {
            return { token0: 'WBTC', token1: 'USDC' }
        }
        if (position.legacyPoolId === 4) {
            return { token0: 'ETH', token1: 'USDT' }
        }
        if (position.legacyPoolId === 5) {
            return { token0: 'USDC', token1: 'USDT' }
        }
        if (position.legacyPoolId === 6) {
            return { token0: 'DAI', token1: 'USDC' }
        }
        if (position.legacyPoolId === 7) {
            return { token0: 'wstETH', token1: 'ETH' }
        }
        if (position.legacyPoolId === 8) {
            return { token0: 'LORDS', token1: 'ETH' }
        }
    }
    if (network === 'goerli-alpha') {
        if (position.legacyPoolId === 1) {
            return { token0: 'ETH', token1: 'USDC' }
        }
        if (position.legacyPoolId === 2) {
            return { token0: 'ETH', token1: 'DAI' }
        }
        if (position.legacyPoolId === 3) {
            return { token0: 'DAI', token1: 'USDC' }
        }
        if (position.legacyPoolId === 4) {
            return { token0: 'ETH', token1: 'wstETH' }
        }
    }
    // console.log('YSYS didnt find dest pool network=', network, ' position=', position)
    return { token0: '', token1: '' }
}

// return the destination fee for the given pool_id
/*function migrateLegacyDestinationFee(network: Network, pool_id: number) {
    if (network === 'goerli-alpha') {
        if (pool_id === 1) return 10000
        // if (pool_id === 1) return 3000
        if (pool_id === 2) return 100
        if (pool_id === 3) return 500
        if (pool_id === 4) return 100
    }
    return 3000
}*/

// return the destination ticks for the given pool_id
export function migrateLegacyDestinationRange(network: Network, pool: CLPool, rangePercent: number) {
    const center = 887272

    // take current tick (which is in range -MAX..MAX)
    // and put it on a spaced tick, and make it in range [0..MAX*2]

    // console.log('YSYS pool=', pool)

    let t = pool.currentTick
    if (t === undefined) throw 'No tick for pool!'
    if (pool.tickSpacing === 0) throw 'No tick spacing!'
    const spaced_center = center + pool.tickSpacing * Math.floor(t / pool.tickSpacing)
    // console.log('YSYS tick=', t, ' spacing=', pool.tickSpacing, ' spaced_center=', spaced_center)
    // convert percent to number of ticks
    // 1.0001**(n_ticks) = 1+percent/100
    const p = Math.log((100 + rangePercent) / 100) / Math.log(1.0001)
    const p_spaced = Math.ceil(p / pool.tickSpacing) * pool.tickSpacing
    return {
        tick_lower: spaced_center - p_spaced - center,
        tick_upper: spaced_center + p_spaced - center
    }
}

export const migrateLegacyPosition_phase0 = async (
    network: Network,
    position: CLPosition,
    _fee: number,
    rangePercent: number
): Promise<{ success: boolean; error?: string; tx: Transaction | undefined }> => {
    const starknet = await getStarknet()

    // ------------------------------------------
    let activeAccount: string = walletAddress()
    if (!activeAccount) {
        return { success: false, tx: undefined }
    }
    if (!starknet?.isConnected) throw Error('starknet wallet not connected')
    // ------------------------------------------

    const fee = _fee * 10000
    try {
        // generate the calls
        // 1. approve the relevant lP token
        // 2. withdraw the entire position from legacy myswap
        // 3. approve both tokens & mint a position in myswap-CL

        const pool_id = position.legacyPoolId
        if (pool_id === undefined) throw 'Unknown legacy pool id'

        // we take 98% slippage hard-coded
        let t0_amount_minus_slippage = BigNumber(position.value.token1AmountWei).multipliedBy('0.98').toFixed(0)
        let t1_amount_minus_slippage = BigNumber(position.value.token2AmountWei).multipliedBy('0.98').toFixed(0)

        let token0 = getToken(network, position.token0)
        let token1 = getToken(network, position.token1)
        // console.log('YSYS fee=', fee, ' token0=', token0, ' token1=', token1)
        if (token0 === undefined || token1 === undefined) throw 'Unknown token0/1'
        const cl_pool = await getAllCLPoolForFee(network, token0, token1, fee)
        cl_pool.currentTick = await getCurrentTick(network, cl_pool.address)
        // console.log('YSYS cl_pool=', cl_pool)
        // check the token order of the pool we are going to migrate into
        let pool_token_order = true
        if (BigNumber(cl_pool.token1.address).eq(BigNumber(position.token0)) && BigNumber(cl_pool.token2.address).eq(BigNumber(position.token1))) {
            // console.log('YSYS token order is good!')
        } else {
            // Not sure why this happens -
            // but if the myswap V1 pool and myswap CL pools have different token orders-
            // we need to flip the CL call (the V1 call remains as the tokens come from there)
            // console.log('YSYS token order is reversed!')
            pool_token_order = false
        }
        const { tick_lower, tick_upper } = migrateLegacyDestinationRange(network, cl_pool, rangePercent)
        let lp_token = getLegacyLpTokenAddresses(network)[pool_id - 1]
        const calls: Array<Call> = [
            {
                contractAddress: lp_token,
                entrypoint: 'approve',
                calldata: [getAMMContract(network), ...toUint256(position.legacyLpTokenAmount ?? '0')]
            },
            {
                contractAddress: getAMMContract(network),
                entrypoint: 'withdraw_liquidity',
                calldata: [pool_id, ...toUint256(position.legacyLpTokenAmount ?? '0'), ...toUint256(t0_amount_minus_slippage), ...toUint256(t1_amount_minus_slippage)]
            },
            {
                contractAddress: token0.address,
                entrypoint: 'approve',
                calldata: [getNFTFactoryAddress(network), ...toUint256(t0_amount_minus_slippage)]
            },
            {
                contractAddress: token1.address,
                entrypoint: 'approve',
                calldata: [getNFTFactoryAddress(network), ...toUint256(t1_amount_minus_slippage)]
            },
            {
                contractAddress: getNFTFactoryAddress(network),
                entrypoint: 'mint',
                calldata: [
                    numberToCallParam(activeAccount),
                    numberToCallParam(pool_token_order ? token0.address : token1.address),
                    numberToCallParam(pool_token_order ? token1.address : token0.address),
                    numberToCallParam(fee),
                    numberToCallParam(tick_lower + 887272),
                    numberToCallParam(tick_upper + 887272),
                    ...toUint256(pool_token_order ? t0_amount_minus_slippage : t1_amount_minus_slippage),
                    ...toUint256(pool_token_order ? t1_amount_minus_slippage : t0_amount_minus_slippage),
                    ...toUint256('0'),
                    ...toUint256('0')
                ]
            }
        ]

        // console.log('YSYS Calls=', calls)
        // @ts-ignore
        let tx = await starknet.account.execute(calls)

        const transaction: Transaction = {
            hash: tx.transaction_hash,
            network,
            status: 'new',
            type: 'migrate_legacy',
            dismiss: false,
            token1: '',
            token1Amount: '',
            token2: '',
            token2Amount: ''
        }

        return { tx: transaction, success: true }
    } catch (e: any) {
        // console.log('YSYS error in phase0 migration: ', e)
    }
    return { success: false, tx: undefined }
}

export type RebalanceSimulationResult = {
    priceImpact: string
    amount0: BigNumber
    amount1: BigNumber

    tick_lower: number
    tick_upper: number

    leftovers_amount0: BigNumber
    leftovers_amount1: BigNumber
}

export const rebalancePosition = async (
    network: Network,
    clPosition: CLPosition,
    currentPoolSqrtPrice: string,
    slippage: number, // in percent. eg 1 = 1%
    simulateOnly: boolean = false,
    activeAccount: string
): Promise<{ success: boolean; error?: string; tx: Transaction | undefined } | RebalanceSimulationResult | undefined> => {
    const starknet = await getStarknet()

    // ------------------------------------------
    // let activeAccount: string = walletAddress()
    // if (!activeAccount) {
    //     return { success: false, tx: undefined }
    // }
    if (!starknet?.isConnected) throw Error('starknet wallet not connected')
    // ------------------------------------------

    try {
        // rebalance accepts price, not sqrt price
        const currentPoolPrice = BigNumber(currentPoolSqrtPrice).dividedBy(BigNumber(2).pow(96)).pow(2).multipliedBy(BigNumber(2).pow(96)).toFixed(0)
        const positionValue = await simulateClosePosition(
            network,
            {
                fee: clPosition!.fee!,
                feeGrowthInside0LastX128: clPosition!.feeGrowthInside0LastX128!,
                feeGrowthInside1LastX128: clPosition!.feeGrowthInside1LastX128!,
                liquidity: clPosition?.liquidity,
                positionIndex: clPosition?.positionIndex,
                tickLower: clPosition?.tickLower,
                tickUpper: clPosition?.tickUpper,
                token0: clPosition?.token0,
                token1: clPosition?.token1,
                tokensOwed0: clPosition?.tokensOwed0,
                tokensOwed1: clPosition?.tokensOwed1
            },
            false,
            activeAccount
        )
        const fees = await simulateCollectFeesOfPosition(
            network,
            {
                fee: clPosition!.fee!,
                feeGrowthInside0LastX128: clPosition!.feeGrowthInside0LastX128!,
                feeGrowthInside1LastX128: clPosition!.feeGrowthInside1LastX128!,
                liquidity: clPosition?.liquidity,
                positionIndex: clPosition?.positionIndex,
                tickLower: clPosition?.tickLower,
                tickUpper: clPosition?.tickUpper,
                token0: clPosition?.token0,
                token1: clPosition?.token1,
                tokensOwed0: clPosition?.tokensOwed0,
                tokensOwed1: clPosition?.tokensOwed1
            },
            activeAccount
        )
        if (positionValue === undefined || fees === undefined) return { success: false, tx: undefined }

        // we take position value minus fees, since the position value is
        // already with the fees, whereas the nft contract calculates
        // slippage before fees.
        const min_amount_a_wei = BigNumber(positionValue.token1AmountWei)
            .minus(BigNumber(fees.token1AmountWei))
            .multipliedBy(1 - slippage / 100)
            .toFixed(0)

        const min_amount_b_wei = BigNumber(positionValue.token2AmountWei)
            .minus(BigNumber(fees.token2AmountWei))
            .multipliedBy(1 - slippage / 100)
            .toFixed(0)

        console.log('YSYSYS slippage=', slippage, ' min_amounts=', min_amount_a_wei, min_amount_b_wei)

        console.log('YSYSYS token_id=', clPosition.positionIndex)
        // generate the call
        const calls: Array<Call> = [
            {
                contractAddress: getNFTFactoryAddress(network),
                entrypoint: 'rebalance',
                calldata: [
                    numberToCallParam(clPosition.positionIndex),
                    numberToCallParam(0),
                    ...toUint256(min_amount_a_wei),
                    ...toUint256(min_amount_b_wei),
                    ...toUint256(currentPoolPrice),
                    numberToCallParam(slippage * 100), // bips
                    numberToCallParam('0') // always rebalance
                    //numberToCallParam('10000') // never rebalance
                ]
            }
        ]

        if (simulateOnly) {
            // when we simulate,
            // we push a pool price read before and after the rebalance -
            // in order to calculate the price impact
            const price_call: Call = {
                entrypoint: 'current_sqrt_price',
                contractAddress: getFactoryAddress(network),
                calldata: [clPosition.poolAddress]
            }
            calls.unshift(price_call)
            calls.push(price_call)

            const position_call: Call = {
                entrypoint: 'positions',
                contractAddress: getNFTFactoryAddress(network),
                calldata: [numberToCallParam(clPosition.positionIndex), numberToCallParam(0)]
            }
            calls.push(position_call)
        }

        const handler = accountCairoVersion() === '1' ? transaction.fromCallsToExecuteCalldata_cairo1 : transaction.fromCallsToExecuteCalldata
        const hcalls = handler(calls)
        const nonce = await (await getProvider(network)).getNonceForAddress(activeAccount)
        let invocation: AccountInvocationItem = {
            signature: [],
            // @ts-ignore
            type: 'INVOKE_FUNCTION',
            nonce: nonce,
            entrypoint: '__execute__',
            contractAddress: activeAccount,
            calldata: hcalls
        }

        if (simulateOnly) {
            // multisig requires change in simulate
            const isBraavosMultisig = accountBraavosMultisig()
            if (isBraavosMultisig) {
                const wrapped_calls = handler([
                    {
                        contractAddress: activeAccount,
                        entrypoint: 'sign_pending_multisig_transaction',
                        calldata: [hcalls.length, ...hcalls, 0, 0, 1]
                    }
                ])
                invocation = {
                    signature: ['0', '0'],
                    // @ts-ignore
                    type: 'INVOKE_FUNCTION',
                    nonce: nonce,
                    entrypoint: '__execute__',
                    contractAddress: activeAccount,
                    calldata: wrapped_calls,
                    version: '340282366920938463463374607431768211457'
                }
            }

            invocation.version = '340282366920938463463374607431768211457'
            try {
                const response = await (
                    await getProvider(network, false)
                ).getSimulateTransaction([invocation], {
                    blockIdentifier: 'pending',
                    skipValidate: true
                })

                // @ts-ignore
                const res = response[0].transaction_trace.execute_invocation.result

                const position_struct_len = 13

                const [idx_start, idx_end] =
                    accountCairoVersion() === '1'
                        ? [res.length - position_struct_len - 14, res.length - position_struct_len - 2]
                        : [res.length - position_struct_len - 12, res.length - position_struct_len - 2]
                const startPrice = fromUint256(res[idx_start], res[idx_start + 1])
                    .dividedBy(BigNumber(2).pow(96))
                    .pow(2)

                const endPrice = fromUint256(res[idx_end], res[idx_end + 1])
                    .dividedBy(BigNumber(2).pow(96))
                    .pow(2)

                const priceImpact = endPrice.minus(startPrice).dividedBy(startPrice).multipliedBy(100).abs()

                // read position after the rebalance
                const pidx = res.length - position_struct_len + 4
                const new_range_low = Number(res[pidx])
                const new_range_high = Number(res[pidx + 1])
                // read the new position amounts
                // from the return value

                const events =
                    accountCairoVersion() === '1'
                        ? // @ts-ignore
                          response[0].transaction_trace.execute_invocation.calls[1].events
                        : // @ts-ignore
                          response[0].transaction_trace.execute_invocation.calls[0].calls[1].events

                const rebalance_key = '0x3b885cf23c3ed31cf487da51fdceda290643d1d839b632557ea1e2c9d6f348b'
                // @ts-ignore
                const reba_event = events.find((event) => event.keys.includes(rebalance_key))
                const token0_amount = fromUint256(reba_event.data[2], reba_event.data[3])
                const token1_amount = fromUint256(reba_event.data[4], reba_event.data[5])
                const token0_leftovers = fromUint256(reba_event.data[6], reba_event.data[7])
                const token1_leftovers = fromUint256(reba_event.data[8], reba_event.data[9])

                return {
                    amount0: token0_amount,
                    amount1: token1_amount,
                    tick_lower: new_range_low,
                    tick_upper: new_range_high,
                    leftovers_amount0: token0_leftovers,
                    leftovers_amount1: token1_leftovers,
                    priceImpact: priceImpact.toFixed(2)
                }
            } catch (error) {
                // Logs the error to the console
                console.error('YSYS An error occurred:', error)
            }
            return undefined
        }

        // @ts-ignore
        let tx = await starknet.account.execute(calls)
        const _transaction: Transaction = {
            hash: tx.transaction_hash,
            network,
            status: 'new',
            type: 'rebalance',
            dismiss: false,
            token1: clPosition.token0,
            token1Amount: clPosition.value.token1Amount,
            token2: clPosition.token1,
            token2Amount: clPosition.value.token2Amount
        }

        return { tx: _transaction, success: true }
    } catch (e: any) {
        console.log('YSYS error in rebalance: ', e)
    }
    return { success: false, tx: undefined }
}
